export type DecimalSign = 'positive' | 'negative' | 'zero' | 'invalid';

const DECIMAL_PATTERN = /^-?\d+(\.\d+)?$/;
/**
 * Display scale from exchanger `currencies.round` / `roundPrecision`
 * (exchanger-front fixtures + cpanel-back Currency.round).
 * USDT/USDC/USD stay at 4 for Paygatex LK (exchanger USDT is 2).
 */
const DISPLAY_DECIMALS_BY_ASSET: Record<string, number> = {
  USDT: 4,
  USDC: 4,
  USD: 4,
  BTC: 6,
  ETH: 4,
  LTC: 3,
  BNB: 3,
  TRX: 2,
};

const DEFAULT_DISPLAY_DECIMALS = 4;

export interface FormatDecimalOptions {
  asset?: string;
  emptyLabel?: string;
  decimals?: number;
  signed?: boolean;
  grouping?: boolean;
  trimTrailingZeros?: boolean;
}

const incrementInteger = (value: string): string => {
  const digits = value.replace(/^0+(?=\d)/, '') || '0';
  const chars = digits.split('');

  for (let index = chars.length - 1; index >= 0; index -= 1) {
    const next = Number(chars[index]) + 1;
    if (next < 10) {
      chars[index] = String(next);
      return chars.join('');
    }
    chars[index] = '0';
  }

  return `1${chars.join('')}`;
};

const incrementFraction = (value: string): { fraction: string; carry: boolean } => {
  const chars = value.split('');

  for (let index = chars.length - 1; index >= 0; index -= 1) {
    const next = Number(chars[index]) + 1;
    if (next < 10) {
      chars[index] = String(next);
      return { fraction: chars.join(''), carry: false };
    }
    chars[index] = '0';
  }

  return { fraction: chars.join(''), carry: true };
};

const roundUnsignedParts = (
  integerPart: string,
  fractionPart: string,
  decimals: number,
): { integerPart: string; fractionPart: string } => {
  const integer = integerPart.replace(/^0+(?=\d)/, '') || '0';

  if (decimals <= 0) {
    const firstFractionDigit = fractionPart[0] ?? '0';
    return {
      integerPart: Number(firstFractionDigit) >= 5 ? incrementInteger(integer) : integer,
      fractionPart: '',
    };
  }

  const padded = fractionPart.padEnd(decimals + 1, '0');
  const kept = padded.slice(0, decimals);
  const remainder = padded.slice(decimals);
  const shouldRoundUp = remainder.length > 0 && remainder[0] >= '5';

  if (!shouldRoundUp) {
    return { integerPart: integer, fractionPart: kept };
  }

  const { fraction, carry } = incrementFraction(kept);
  if (carry) {
    return { integerPart: incrementInteger(integer), fractionPart: '0'.repeat(decimals) };
  }

  return { integerPart: integer, fractionPart: fraction };
};

const formatIntegerPart = (integerPart: string, grouping: boolean): string => {
  const normalized = integerPart.replace(/^0+(?=\d)/, '') || '0';
  if (!grouping) {
    return normalized;
  }

  return normalized.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

const formatFractionPart = (fractionPart: string, trimTrailingZeros: boolean): string => {
  if (!fractionPart) {
    return '';
  }

  if (!trimTrailingZeros) {
    return fractionPart;
  }

  return fractionPart.replace(/0+$/, '');
};

export const getDisplayDecimals = (currencyCode?: string | null): number => {
  const code = (currencyCode ?? '').trim().toUpperCase();
  if (!code) {
    return DEFAULT_DISPLAY_DECIMALS;
  }

  return DISPLAY_DECIMALS_BY_ASSET[code] ?? DEFAULT_DISPLAY_DECIMALS;
};

export const getDecimalSign = (value: string | number | null | undefined): DecimalSign => {
  if (value == null) {
    return 'invalid';
  }

  const trimmed = String(value).trim();
  if (!trimmed || !DECIMAL_PATTERN.test(trimmed)) {
    return 'invalid';
  }

  if (/^-?0+(\.0+)?$/.test(trimmed)) {
    return 'zero';
  }

  return trimmed.startsWith('-') ? 'negative' : 'positive';
};

export const isPositiveDecimal = (value: string | number | null | undefined): boolean =>
  getDecimalSign(value) === 'positive';

export const isNegativeDecimal = (value: string | number | null | undefined): boolean =>
  getDecimalSign(value) === 'negative';

/** Format a decimal without converting the full value through JS number. */
export const formatDecimal = (
  value: string | number | null | undefined,
  options?: FormatDecimalOptions,
): string => {
  const emptyLabel = options?.emptyLabel ?? '—';
  const grouping = options?.grouping ?? true;

  if (value == null) {
    return emptyLabel;
  }

  const trimmed = String(value).trim();
  if (!trimmed || !DECIMAL_PATTERN.test(trimmed)) {
    return emptyLabel;
  }

  const sign = getDecimalSign(trimmed);
  const negative = sign === 'negative';
  const unsigned = negative ? trimmed.slice(1) : trimmed;
  const [rawIntegerPart, rawFractionPart = ''] = unsigned.split('.');
  const decimals = options?.decimals;
  const trimTrailingZeros = options?.trimTrailingZeros ?? decimals == null;

  const rounded =
    decimals == null
      ? { integerPart: rawIntegerPart.replace(/^0+(?=\d)/, '') || '0', fractionPart: rawFractionPart }
      : roundUnsignedParts(rawIntegerPart, rawFractionPart, decimals);

  const formattedInteger = formatIntegerPart(rounded.integerPart, grouping);
  const formattedFraction = formatFractionPart(rounded.fractionPart, trimTrailingZeros);
  const body = formattedFraction ? `${formattedInteger}.${formattedFraction}` : formattedInteger;
  let formatted = negative ? `-${body}` : body;

  if (options?.signed && sign === 'positive') {
    formatted = `+${formatted}`;
  }

  if (options?.asset) {
    return `${formatted} ${options.asset}`;
  }

  return formatted;
};
